import { Expo, TimelineLite, TimelineMax } from "gsap";
import * as HLF from "html-living-framework";

import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import { Culture } from "../Data/Culture";
import * as SFX from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VO from "../Data/VoiceOver";

import { AfterGame } from "./AfterGame";

import { Character } from "../GameObjects/Character";
import CharacterManager from "../GameObjects/CharacterManager";
import CultureBackgroundManager from "../GameObjects/CultureBackgroundManager";
import { DanceMeter } from "../GameObjects/DanceMeter";
import EffectsButtons from "../GameObjects/EffectsButtons";
import Flag from "../GameObjects/Flag";
import Globe from "../GameObjects/Globe";
import { InstrumentKeyboard } from "../GameObjects/InstrumentKeyboard";
import { KeyboardFocusManager } from "../GameObjects/KeyboardFocusManager";
import { Luna } from "../GameObjects/Luna";
import MusicManager from "../GameObjects/MusicManager";
import { OnboardingManager } from "../GameObjects/OnboardingManager";
import PhotoButton from "../GameObjects/PhotoButton";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";
import Gameplay from "../Gameplay";

let GLOBE_VO_HINT_DELAY = 25;
const GLOBE_VO_HINT_DELAY_2 = 45;

export class Game extends HLF.State {
// static vars
    public static screenshot: PIXI.RenderTexture;
    private static didPlayTutorial = false;
// end

// child gameobjects
    protected readonly _bg                      = this.addChild(HLF.PixiUtils.makeSprite("menu-bg"));
    protected readonly _luna                    = this.addChild(new Luna(new PIXI.Point(10, 450)));
    protected readonly _danceMeter              = this.addChild(new DanceMeter());
    protected readonly _keyboardFocusManager    = this.addChild(new KeyboardFocusManager());
    protected readonly _sfxButtonsContainer     = this.addChild(new EffectsButtons());
    protected readonly _onboardingManager       = this.addChild(new OnboardingManager());
    protected readonly _musicManager            = this.addChild(new MusicManager());
    protected readonly _backgroundsContainer    = this.addChild(new CultureBackgroundManager());
    protected readonly _characterManager        = this.addChild(new CharacterManager());
    protected readonly _buttonCultureChange     = this.addChild(new Globe());
    protected readonly _instrumentKeyboard      = this.addChild(new InstrumentKeyboard());
    protected readonly _photoButton         = this.addChild(new PhotoButton(this.OnScreenshotButtonPressed.bind(this)));
//////

// bound functions
    protected _onKeyPressedCallback = this.onKeyPressed.bind(this);
    protected _messageTokens: any[];
    protected _timeouts: any[] = [];
// end

// state variables
    protected _inDebugMode: boolean = Application.instance.debugMode;
    protected _introFinished: boolean = false;
    // private readonly _factLoaded: boolean = true;
// end
    private _globeHintTimeline?: TimelineMax;

    constructor() {
        super();

        // event listeners
        this._messageTokens = [
            PubSub.subscribe(Topics.ALL_INSTRUMENT_SWAPS_COMPLETE,  this.MaybeCompleteCultureChange.bind(this)),
            PubSub.subscribe(Topics.ALL_BG_SWAPS_COMPLETE,          this.MaybeCompleteCultureChange.bind(this)),
            PubSub.subscribe(Topics.INTRO_COMPLETED,                this.onIntroComplete.bind(this)),
            PubSub.subscribe(Topics.ENABLE_INPUT_CHARACTERS,        this.EnableCharactersInput.bind(this)),
            PubSub.subscribe(Topics.ENABLE_INPUT,                   this.EnableInput.bind(this)),
            PubSub.subscribe(Topics.DISABLE_INPUT,                  this.DisableInput.bind(this)),
        ];

        if (PIXI.utils.isMobile.any === false) {
            window.addEventListener(HLF.Input.Events.KEY_DOWN, this._onKeyPressedCallback, false);
        }

        // z order
        this.setChildIndex(this._backgroundsContainer,  1);
        this.setChildIndex(this._luna,                  2);
        this.setChildIndex(this._characterManager,      3);
        this.setChildIndex(this._danceMeter,            4);
        this.setChildIndex(this._instrumentKeyboard,    5);
        this.setChildIndex(this._sfxButtonsContainer,   6);
        this.setChildIndex(this._photoButton,           7);
        this.setChildIndex(this._buttonCultureChange,   8);
        this.setChildIndex(this._onboardingManager,     9);
        this.setChildIndex(this._keyboardFocusManager, 10);

        this._instrumentKeyboard.visible = false;
        this._backgroundsContainer.visible = false;
        if (Gameplay.Instance.characterManager?.characters !== undefined) {
            for (let i = Gameplay.Instance.characterManager.characters.length - 1; i >= 0; --i) {
                if (Gameplay.Instance.characterManager.characters[i] !== undefined) {
                    Gameplay.Instance.characterManager.characters[i].visible = false;
                }
            }
        }

        if (!Game.didPlayTutorial) {
            this.SetupOnboarding();
        }
    }

    public init(pSize: PIXI.Point) {
        super.init(pSize);
        this.onResize(pSize);

        // disable all input
        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
        PubSub.publishSync(Topics.DISABLE_INPUT, undefined);
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        Application.instance.audio.stop(Constants.MUSIC_LAYER_TOP, HLF.AudioCategory.BGM);
        Application.instance.audio.stop(Constants.MUSIC_LAYER_BASE, HLF.AudioCategory.BGM);
        this._messageTokens.forEach(PubSub.unsubscribe);
        this._timeouts.forEach(clearTimeout);
        window.removeEventListener(HLF.Input.Events.KEY_DOWN, this._onKeyPressedCallback, false);
        super.destroy(pOptions);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this._instrumentKeyboard.onResize(pSize);
        if (Gameplay.Instance.characterManager?.characters !== undefined) {
            for (let i = Gameplay.Instance.characterManager.characters.length - 1; i >= 0; --i) {
                if (Gameplay.Instance.characterManager.characters[i] !== undefined) {
                    Gameplay.Instance.characterManager.characters[i].onResize(pSize);
                }
            }
        }

        this._backgroundsContainer.onResize(pSize);

        this._buttonCultureChange.position.x = this._size.x / 2 - this._buttonCultureChange.width / 2 - 40;

        this._sfxButtonsContainer.onResize(pSize);

        this._onboardingManager.onResize();

        this._photoButton.position.set(200, 80);
    }

    public animateIn(pOnComplete: () => void): void {
        let hasUserPlayedAnySolo = false;
        const once = PubSub.subscribe(Topics.SOLO_REQUESTED, () => {
            hasUserPlayedAnySolo = true;
            PubSub.unsubscribe(once);
        });

        // We’re putting on a concert with instruments from all around the world!
        // And you can play too! The more we play, the more Luna dances!
        Application.instance.voiceOver.play(VO.O6, InteruptType.EQUAL, VoiceCategory.INSTRUCTIONAL, () => {
            if (!hasUserPlayedAnySolo) {
                // "Select Andy, Leo, or me Carmen and we’ll play a tune for you."
                Application.instance.voiceOver.play(VO.O1, InteruptType.EQUAL, VoiceCategory.INSTRUCTIONAL);
            }
        });

        // play entry animations
        PubSub.publishSync(Topics.START_INTRO, undefined);
        this._instrumentKeyboard.visible = true;
        this._backgroundsContainer.visible = true;
        if (Gameplay.Instance.characterManager?.characters !== undefined) {
            for (let i = Gameplay.Instance.characterManager.characters.length - 1; i >= 0; --i) {
                if (Gameplay.Instance.characterManager.characters[i] !== undefined) {
                    Gameplay.Instance.characterManager.characters[i].visible = true;
                }
            }
        }

        super.animateIn(pOnComplete);
    }

    public animateOut(pOnComplete: () => void): void {
// TODO: move this code into its own method
        const flash = new PIXI.Sprite(PIXI.Texture.WHITE);
        flash.width  = this._size.x;
        flash.height = this._size.y;
        flash.anchor.set(0.5);
        flash.position.set(0, 0);
        flash.alpha = 0;
        this.addChild(flash);
        new TimelineLite()
            .to(flash, 0.1, {pixi: {alpha: 1}})
            .to(flash, 1.5, {pixi: {alpha: 0}, ease: Expo.easeOut})
            .call(pOnComplete);
    }

    public update(pDeltaTime: number) {
        if (Gameplay.Instance.characterManager?.characters !== undefined) {
            for (let i = Gameplay.Instance.characterManager.characters.length - 1; i >= 0; --i) {
                if (Gameplay.Instance.characterManager.characters[i] !== undefined) {
                    Gameplay.Instance.characterManager.characters[i].update(pDeltaTime);
                }
            }
        }

        this._luna.update(pDeltaTime);
        this._instrumentKeyboard.updateFromGame(pDeltaTime);
        this._musicManager.update(pDeltaTime);
        this._buttonCultureChange.update(pDeltaTime);
        this._danceMeter.update(pDeltaTime);
    }

    protected EnableInput() {
        this.EnableCharactersInput();
        this.EnableLunaInput();
        this.EnableInstrumentsInput();
        this.EnableGlobeInput();
        this.EnableSfxButtonsInput();
    }

    protected DisableInput() {
        this.DisableLunaInput();
        this.DisableCharactersInput();
        this.DisableInstrumentsInput();
        this.DisableGlobeInput();
        this.DisableSfxButtonsInput();
    }

    protected onIntroComplete() {
        this._introFinished = true;
        this.EnableCharactersInput(); // just in case
        if (!Game.didPlayTutorial) {
            Game.didPlayTutorial = true;
            this._onboardingManager.start();
        }
        else {
            this._danceMeter.startIntro();
            this._buttonCultureChange.startIntro();
            this.EnableInput();
        }
    }

    protected MaybeCompleteCultureChange() {
        if (this._characterManager.swapComplete && this._backgroundsContainer.swapComplete) {
            PubSub.publishSync(Topics.CULTURE_CHANGE_COMPLETE, Gameplay.Instance.currentCulture);
        }
    }

    private EnableSfxButtonsInput() {
        this._sfxButtonsContainer.buttons.forEach((b) => b.locked = false);
    }

    private EnableGlobeInput() {
        this._buttonCultureChange.changeCultureButtonInputState(true, true);
    }

    private EnableInstrumentsInput() {
        this._instrumentKeyboard.inputEnabled = true;
    }

    private EnableCharactersInput() {
        if (Gameplay.Instance.characterManager?.characters !== undefined) {
            for (let j = Gameplay.Instance.characterManager.characters.length - 1; j >= 0; --j) {
                Gameplay.Instance.characterManager.characters[j].inputEnabled = true;
            }
        }
    }

    private EnableLunaInput() {
        this._luna.interactive = true;
    }
    private DisableSfxButtonsInput() {
        this._sfxButtonsContainer.buttons.forEach((b) => b.locked = true);
    }

    private DisableGlobeInput() {
        this._buttonCultureChange.changeCultureButtonInputState(false, true);
    }
    private DisableInstrumentsInput() {
        this._instrumentKeyboard.inputEnabled = false;
    }
    private DisableCharactersInput() {
        if (Gameplay.Instance.characterManager?.characters !== undefined) {
            for (let j = Gameplay.Instance.characterManager.characters.length - 1; j >= 0; --j) {
                Gameplay.Instance.characterManager.characters[j].inputEnabled = false;
            }
        }
    }
    private DisableLunaInput() {
        this._luna.interactive = false;
    }

    // TODO: move this into a different file
    private SetupOnboarding() {
        // step 1. click on carmen, andy, or leo
        this._onboardingManager.addStep(
            Topics.SOLO_REQUESTED,      // action we're waiting for
            undefined,                  // change this if we ever want to force them to click a specific character
            this._characterManager.characters, // target(s) of the arrows
            0,                          // delay before showing arrows
            true,                       // don't allow this step to be completed out of order
            undefined,                  // unused
            () => {                     // success callback
                console.log("onboarding step 1a complete");
                this.DisableCharactersInput(); // disable characters on click, until step 1a completes
            });

        // step 1b. show dance meter
        this._onboardingManager.addStep(Topics.SOLO_STARTED, undefined, [], 0, true, undefined, () => {
            this._danceMeter.startIntro();
        });

        // step 1c. wait for solo to complete
        this._onboardingManager.addStep(
            Topics.SOLO_ENDED, undefined, [], // target is empty array
            0, true, undefined,
            () => {
                console.log("onboarding step 1c complete");
                // wait for luna VO to finish
                this._timeouts.push(setTimeout(() => {
                    // "Select any instrument at the bottom to hear how it sounds."
                    Application.instance.voiceOver.play(VO.O2, InteruptType.NONE, VoiceCategory.INSTRUCTIONAL);
                    this.EnableInstrumentsInput(); // enable targets of next step
                }, 2000));
            },
        );

        const delayBeforeShowingGlobe = 10; // seconds
        this._globeHintTimeline = new TimelineMax();
        this._globeHintTimeline
            .repeat(-1)
            // hide hint after 3 seconds
            .call(() => {
                PubSub.publishSync(Topics.FORCE_HIDE_ONBOARDING, undefined);
            }, undefined, undefined, 3)
            // show hint 22 or 42 seconds later
            .call(() => {
                Application.instance.voiceOver.play(VO.O4, InteruptType.LESS, VoiceCategory.GAMEPLAY);
                PubSub.publishSync(Topics.FORCE_ONBOARDING_HINT, undefined);
                GLOBE_VO_HINT_DELAY = GLOBE_VO_HINT_DELAY_2;
            }, undefined, undefined, GLOBE_VO_HINT_DELAY)
            .pause(0, true)
        ;

        // step 2: wait for sample pad click
        this._onboardingManager.addStep(
            Topics.SAMPLE_PAD_BUTTON_CLICKED,
            undefined,
            this._instrumentKeyboard.keys,
            2, // delay before showing arrows
            true, undefined, () => {
                console.log("onboarding step 2 complete");
                this.EnableCharactersInput();

                // delay so that the sample pad's hover VO can finish
                this._timeouts.push(setTimeout(() => {
                    // "Now it’s your turn to create beautiful music."
                    const didItWork = Application.instance.voiceOver.play(
                        VO.O5, InteruptType.NONE, VoiceCategory.INSTRUCTIONAL);

                    if (!didItWork) {
                        // wait 3 seconds and try playing the VO again
                        this._timeouts.push(setTimeout(() => {
                            Application.instance.voiceOver.play(VO.O5, InteruptType.LESS, VoiceCategory.INSTRUCTIONAL);
                        }, 3000));
                    }
                }, 1000));

                // delay before showing globe
                this._timeouts.push(setTimeout(() => {
                    // PubSub.publishSync(Topics.DISABLE_INPUT, undefined);
                    this._buttonCultureChange.startIntro(); // TODO: use pubsub here instead
                    this._timeouts.push(setTimeout(() => {
                        this.EnableGlobeInput();
                        // "Tap the Magic Globe to change the style of music!"
                        // tslint:disable-next-line: max-line-length
                        Application.instance.voiceOver.play(VO.O4, InteruptType.LESS, VoiceCategory.INSTRUCTIONAL, () => {
                            this._globeHintTimeline?.play(0);
                        });
                    }, 2000));
                }, delayBeforeShowingGlobe * 1000));
        });

        // step 3a: click on the globe
        this._onboardingManager.addStep(Topics.CULTURE_BUTTON_PRESSED,
            undefined,
            this._buttonCultureChange,
            delayBeforeShowingGlobe + 2, // wait for globe to animate into position
            true, undefined, () => {
                this._globeHintTimeline?.kill();
                console.log("onboarding step 3a complete");
        });

        // step 3b: wait for solo to finish
        this._onboardingManager.addStep(Topics.SOLO_ENDED,
            undefined, [], 5, true, undefined, () => {
                console.log("onboarding step 3b complete");
                PubSub.publishSync(Topics.ENABLE_INPUT, undefined);
                // free play mode now for a bit
        });
    }

    private onKeyPressed(pEvent: KeyboardEvent): void {
        if (pEvent.keyCode === HLF.Input.KeyCodes.ONE) {
            Gameplay.Instance.characterManager!.characters[0].simulateClick();
        }
        else if (pEvent.keyCode === HLF.Input.KeyCodes.TWO) {
            Gameplay.Instance.characterManager!.characters[1].simulateClick();
        }
        else if (pEvent.keyCode === HLF.Input.KeyCodes.THREE) {
            Gameplay.Instance.characterManager!.characters[2].simulateClick();
        }
        else if (pEvent.keyCode === HLF.Input.KeyCodes.Q) {
            this.OnScreenshotButtonPressed();
        }
        else {
            this._instrumentKeyboard.onKeyPressed(pEvent);
        }
    }

    private OnScreenshotButtonPressed() {
        this._onboardingManager.cleanup();
        this._onboardingManager.clear();
        this._globeHintTimeline?.kill();
        this.hideUI();
        this.DisableInput();
        this._timeouts.forEach((t) => {clearTimeout(t); });
        this._messageTokens.forEach(PubSub.unsubscribe);
        Application.instance.audio.play(SFX.CAMERA_CLICKED, 1, false, HLF.AudioCategory.SFX);
        Application.instance.analytics.event("photo", "take");

        this.takeScreenshot();
        this.goToNextState();
    }

    private hideUI() {
        this._photoButton.visible = false;
        this._instrumentKeyboard.visible = false;
        this._sfxButtonsContainer.buttons.forEach((b) => b.visible = false);
        this._danceMeter.visible = false;
        this._keyboardFocusManager.visible = false;
    }

    private takeScreenshot() {
        const width = 1024;
        const height = 512;
        const offset = new PIXI.Matrix();
        offset.tx = (width - this._size.x) / 2;
        Game.screenshot = PIXI.RenderTexture.create(width, height);
        Application.instance.renderer.render(this.parent, Game.screenshot, true, offset);
    }

    private goToNextState() {
        PubSub.publishSync(HLF.UNREGISTER_ALL_FOCUSABLES, undefined);
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
            AfterGame.ID,
            undefined,
            HLF.TransitionStep.LoadAssets, // just in case, but there should be zero new assets to load
            HLF.TransitionStep.AttachNewInFront,
            HLF.TransitionStep.AnimSimultaneously,
            HLF.TransitionStep.RemoveCurrent,
            // NOTE that we are intentionally _not_ unloading old assets
        ));
    }
}
